import { Col, Row } from "antd";
import { get, isEmpty, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdAdminPanelSettings,
  MdOutlineFamilyRestroom,
  MdWorkOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_EXERCISE_FRAME,
  FAMILY_SITUATION_LMF,
  SITUATION_PROFESSIONAL,
  SITUATION_PROFESSIONAL_INDEPENDENT,
  SITUATION_PROFESSIONAL_RETIRED,
  SITUATION_PROFESSIONAL_SALARIED_PRIVATE_SECTOR,
  SITUATION_PROFESSIONAL_SALARIED_PUBLIC_SECTOR,
  SITUATION_PROFESSIONAL_STUDENT,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

export const showExerciseFrame = (value) =>
  [
    SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
    SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
    SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
  ].includes(value);

const getSubcategoriesForCategory = (category) => {
  switch (category) {
    case SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR:
      return SITUATION_PROFESSIONAL_SALARIED_PRIVATE_SECTOR;
    case SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR:
      return SITUATION_PROFESSIONAL_SALARIED_PUBLIC_SECTOR;
    case SITUATION_PROFESSIONAL.INDEPENDENT:
      return SITUATION_PROFESSIONAL_INDEPENDENT;
    case SITUATION_PROFESSIONAL.STUDENT:
      return SITUATION_PROFESSIONAL_STUDENT;
    case SITUATION_PROFESSIONAL.RETIRED:
      return SITUATION_PROFESSIONAL_RETIRED;
    default:
      return {};
  }
};

const LMF_SITUATION_PROFESSIONAL = {
  [SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR]:
    SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR,
  [SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR]:
    SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR,
  [SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB]:
    SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
  [SITUATION_PROFESSIONAL.STUDENT]: SITUATION_PROFESSIONAL.STUDENT,
  [SITUATION_PROFESSIONAL.INDEPENDENT]: SITUATION_PROFESSIONAL.INDEPENDENT,
  [SITUATION_PROFESSIONAL.RETIRED]: SITUATION_PROFESSIONAL.RETIRED,
};

const Activity = ({
  formProps,
  values,
  setPercent,
  setFieldValue,
  disabled,
  setDisabled,
  isFieldRequired,
  initialValues,
  errors,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Input
              label={SUBSCRIPTION.FORM.PROFILE.LABEL}
              icon={<MdAdminPanelSettings size={16} />}
              defaultValue={
                SUBSCRIPTION.FORM.PROFILE.OPTIONS[
                  get(initialValues, "project.contact.fields.profile")
                ]
              }
              disabled={true}
            />
          </Col>
          <Col lg={24} xs={24}>
            <Select
              options={Object.entries(LMF_SITUATION_PROFESSIONAL).map(
                ([key, value]) => ({
                  value,
                  label: SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.OPTIONS[key],
                }),
              )}
              label={SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.LABEL}
              icon={<MdWorkOutline size={16} />}
              placeholder={SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.PLACEHOLDER}
              defaultValue={get(
                initialValues,
                "project.contact.socioProfessionalCategory",
              )}
              errors={errors["project.contact.socioProfessionalCategory"]}
              touched={
                !isEmpty(errors["project.contact.socioProfessionalCategory"])
              }
              onChange={(value) => {
                set(
                  initialValues,
                  "project.contact.socioProfessionalCategory",
                  value,
                );
                set(
                  initialValues,
                  "project.contact.fields.socioProfessionalSubCategory",
                  null,
                );
                if (
                  (
                    get(
                      initialValues,
                      "insurancePlan.config.additionalDocuments",
                      [],
                    ) || []
                  ).includes("proof.pensionFund")
                ) {
                  setDisabled((prev) => ({
                    ...prev,
                    [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]:
                      value === SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
                  }));
                }

                if (!showExerciseFrame(value))
                  set(
                    initialValues,
                    "project.contact.exerciseFrame",
                    CONTACT_EXERCISE_FRAME.EMPLOYEE,
                  );
                setDisabled((prev) => ({
                  ...prev,
                  project: {
                    ...prev.project,
                    contact: {
                      ...prev.project.contact,
                      exerciseFrame: !showExerciseFrame(value),
                    },
                  },
                }));
              }}
              // onBlur={() => setPercent(initialValues)}
              optional={SUBSCRIPTION.FORM.OPTIONAL}
              // required={isFieldRequired(
              //   "project.contact.socioProfessionalCategory"
              // )}
            />
          </Col>

          {get(initialValues, "project.contact.socioProfessionalCategory") &&
            get(initialValues, "project.contact.socioProfessionalCategory") !==
              SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB &&
            Object.keys(
              getSubcategoriesForCategory(
                get(initialValues, "project.contact.socioProfessionalCategory"),
              ),
            ).length > 0 && (
              <Col lg={24} xs={24}>
                <Select
                  key={get(
                    initialValues,
                    "project.contact.socioProfessionalCategory",
                  )}
                  options={(() => {
                    const selectedCategory = get(
                      initialValues,
                      "project.contact.socioProfessionalCategory",
                    );
                    const subcategories =
                      getSubcategoriesForCategory(selectedCategory);

                    return Object.entries(subcategories).map(
                      ([key, value]) => ({
                        value,
                        label:
                          SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.SUB_OPTIONS[
                            selectedCategory
                          ]?.OPTIONS?.[key] || key,
                      }),
                    );
                  })()}
                  label={
                    SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.SUB_OPTIONS[
                      get(
                        initialValues,
                        "project.contact.socioProfessionalCategory",
                      )
                    ]?.LABEL || "Précision sur la situation professionnelle"
                  }
                  icon={<MdWorkOutline size={16} />}
                  placeholder={`${SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.SUB_OPTIONS.PLACEHOLDER}${SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.SUB_OPTIONS[get(initialValues, "project.contact.socioProfessionalCategory")]?.LABEL?.toLowerCase() || "catégorie"}`}
                  defaultValue={(() => {
                    const currentCategory = get(
                      initialValues,
                      "project.contact.socioProfessionalCategory",
                    );
                    const currentSubCategory = get(
                      initialValues,
                      "project.contact.fields.socioProfessionalSubCategory",
                    );

                    if (!currentCategory || !currentSubCategory) return null;

                    const validSubcategories =
                      getSubcategoriesForCategory(currentCategory);

                    const isValidSubCategory =
                      Object.values(validSubcategories).includes(
                        currentSubCategory,
                      );
                    return isValidSubCategory ? currentSubCategory : null;
                  })()}
                  errors={
                    errors[
                      "project.contact.fields.socioProfessionalSubCategory"
                    ]
                  }
                  touched={
                    !isEmpty(
                      errors[
                        "project.contact.fields.socioProfessionalSubCategory"
                      ],
                    )
                  }
                  onChange={(value) => {
                    set(
                      initialValues,
                      "project.contact.fields.socioProfessionalSubCategory",
                      value,
                    );
                  }}
                  // onBlur={() => setPercent(initialValues)}
                  // required={true}
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                />
              </Col>
            )}

          <Col lg={24} xs={24}>
            <Select
              label={SUBSCRIPTION.FORM.FAMILY_SITUATION.LABEL}
              optional={SUBSCRIPTION.FORM.OPTIONAL}
              icon={<MdOutlineFamilyRestroom size={16} />}
              placeholder={SUBSCRIPTION.FORM.FAMILY_SITUATION.PLACEHOLDER}
              type="simple"
              options={Object.entries(FAMILY_SITUATION_LMF).map(
                ([key, value]) => ({
                  value,
                  label: SUBSCRIPTION.FORM.LMF_FAMILY_SITUATION.OPTIONS[key],
                }),
              )}
              defaultValue={get(
                initialValues,
                "project.contact.fields.familySituation",
              )}
              errors={errors["project.contact.fields.familySituation"]}
              touched={
                !isEmpty(errors["project.contact.fields.familySituation"])
              }
              onChange={(value) => {
                set(
                  initialValues,
                  "project.contact.fields.familySituation",
                  value,
                );
                // setPercent(initialValues);
              }}
              // required={isFieldRequired(
              //   "project.contact.fields.familySituation"
              // )}
            />
          </Col>
          {/* <Col lg={24} xs={24}>
            <Select
              options={Object.entries(JOB_LIST).map(([key, value]) => ({
                label: SUBSCRIPTION.FORM.PROFESSION.LIST[key],
                value,
              }))}
              icon={<MdWorkOutline size={16} />}
              placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
              label={SUBSCRIPTION.FORM.PROFESSION.LABEL}
              defaultValue={get(initialValues, "project.contact.profession")}
              errors={errors["project.contact.profession"]}
              touched={!isEmpty(errors["project.contact.profession"])}
              onChange={(value) => {
                set(initialValues, "project.contact.profession", value);
              }}
              onBlur={() => setPercent(initialValues)}
              optional={
                !get(disabled, "project.contact.profession") &&
                !isFieldRequired("project.contact.profession") &&
                SUBSCRIPTION.FORM.OPTIONAL
              }
              required={isFieldRequired("project.contact.profession")}
              disabled={get(disabled, "project.contact.profession")}
            />
          </Col> */}
          {/* {get(initialValues, "project.contact.profession") ===
            JOB_LIST.HOSPITAL_CIVIL_SERVICE && (
              <Col lg={24} xs={24}>
                <Input
                  type="date"
                  variant="simple"
                  optional
                  label={SUBSCRIPTION.FORM.ENTRY_DATE.LABEL}
                  icon={<MdCalendarMonth size={16} />}
                  placeholder={SUBSCRIPTION.FORM.ENTRY_DATE.PLACEHOLDER}
                  defaultValue={
                    get(initialValues, "project.contact.fields.entryDate") &&
                    dayjs(get(initialValues, "project.contact.fields.entryDate"))
                  }
                  errors={errors["project.contact.fields.entryDate"]}
                  touched={!isEmpty(errors["project.contact.fields.entryDate"])}
                  onChange={(date) => {
                    set(initialValues, "project.contact.fields.entryDate", date);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  format={DATE_FORMAT}
                  required={isFieldRequired("project.contact.fields.entryDate")}
                  disabledDate={(current) => current >= dayjs().startOf("day")}
                />
              </Col>
            )}
          {!get(disabled, "project.contact.optMadelin") && (
            <>
              <Col lg={24} xs={24}>
                <Input
                  type="radio"
                  variant="group"
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  icon={<MdOutlineSouthAmerica size={16} />}
                  label={SUBSCRIPTION.FORM.MADELIN_LAW.LABEL}
                  values={[
                    {
                      value: true,
                      label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.YES,
                    },
                    {
                      value: false,
                      label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.NO,
                    },
                  ]}
                  defaultValue={get(
                    initialValues,
                    "project.contact.optMadelin"
                  )}
                  errors={errors["project.contact.optMadelin"]}
                  touched={!isEmpty(errors["project.contact.optMadelin"])}
                  onChange={(value) =>
                    set(initialValues, "project.contact.optMadelin", value)
                  }
                  required={isFieldRequired("project.contact.optMadelin")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Alert
                  showIcon
                  type="info"
                  message={SUBSCRIPTION.FORM.MADELIN_LAW.INFO}
                />
              </Col>
            </>
          )} */}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
